import { useSetUrlState } from "hooks/useSetUrlState";
import { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import CustomTooltip from "./popover/Tooltip";

type HeaderProps = {
  view: string;
  setView: (view: "year" | "month" | "day") => void;
  setLabView: Dispatch<SetStateAction<"heatMap" | "lineChart">>;
  labView: string;
  showEncounter: string;
  handleCreateClick: () => void;
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
};

type ToggleButtonProps = {
  isActive: boolean;
  name: string;
  onClick: () => void;
};
const ToggleButton = ({ isActive, name, onClick }: ToggleButtonProps) => {
  return (
    <button
      className={`min-w-[42px] px-3 h-[18px] ${
        isActive
          ? "text-white bg-[#A82784] font-medium rounded"
          : " font-semibold"
      } text-xs`}
      onClick={onClick}
    >
      {name}
    </button>
  );
};

/**
 * The header is the top most section on the application containing the following
 * - The search bar: Used to search for text or keyword on the tiles to enable quick reference
 * - The view toggle buttons: Allows users to toggle between the year, month and day view of the timeline. It can be used in place of the zoom to quickly jump between each zoom levels
 * - The encounter toggle: This is the eye icon on the header that enables users hide and show the encounter overlay
 * - The data side pane toggle: This hides and show the side pane for entering data
 */

const Header = ({
  view,
  setView,
  showEncounter,
  handleCreateClick,
  setSearchTerm,
  searchTerm,
  labView,
  setLabView,
}: HeaderProps) => {
  const { setUrlState, deleteUrlState } = useSetUrlState();

  const iconPath = useMemo(
    () =>
      showEncounter === "true"
        ? "/icons/eye-closed-icon.svg"
        : "/icons/eye-open-icon.svg",
    [showEncounter]
  );

  const handleToggleEncounters = useCallback(() => {
    if (showEncounter === "true") {
      deleteUrlState("showEncounters");
    } else {
      setUrlState("showEncounters", "true");
    }
  }, [deleteUrlState, setUrlState, showEncounter]);

  return (
    <div className="w-full border-b  h-12 flex justify-between items-center sticky top-0 z-10 bg-white">
      <div className="flex gap-10">
        <div className="bg-[#EFF1F3] h-[33px] px-[7px] gap-2 rounded-lg ml-4 w-[fit border border-[#E5E5E5] flex items-center justify-between">
          <ToggleButton
            onClick={() => setView("year")}
            name="Year"
            isActive={view === "year"}
          />
          <ToggleButton
            onClick={() => setView("month")}
            name="Month"
            isActive={view === "month"}
          />
          <ToggleButton
            onClick={() => setView("day")}
            name="Day"
            isActive={view === "day"}
          />
        </div>

        <div className="bg-[#EFF1F3] h-[33px] px-[7px] rounded-lg ml-4 w-[fit border border-[#E5E5E5] flex items-center justify-between">
          <ToggleButton
            onClick={() => setLabView("heatMap")}
            name="Heat Map"
            isActive={labView === "heatMap"}
          />
          <ToggleButton
            onClick={() => setLabView("lineChart")}
            name="Line Chart "
            isActive={labView === "lineChart"}
          />
        </div>
      </div>
      <div className="flex gap-6 items-center">
        <form action="">
          <input
            type="search"
            id=""
            className="w-[200px] h-[30px] border text-xs px-2 rounded-lg"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </form>

        <div className="gap-2 flex items-center justify-between  mr-4">
          <CustomTooltip followCursor title={<div>Toggle Encounters</div>}>
            <button
              onClick={handleToggleEncounters}
              className="w-8 h-8 rounded-full border bg-[#EFF1F3] flex items-center justify-center"
            >
              <img
                src={iconPath}
                alt="Show Encounter"
                height={20}
                width={20}
                className=""
              />
            </button>
          </CustomTooltip>
          <CustomTooltip followCursor title={<div>Enter new Data</div>}>
            <button onClick={handleCreateClick} className="mt-2">
              <img
                src="/icons/plus-icon-circle-dark.svg"
                alt="plus-button"
                height={45}
                width={45}
                className=""
              />
            </button>
          </CustomTooltip>

          <div className="ml-6">
            <img
              src="/images/vehr-logo.png"
              alt="vher-logo"
              width={30}
              height={30}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
